import type { NotificationRecipients } from '@axilon-apps/domains-ontology-ui-state'
import { RecipientsField } from '../Notifications/RecipientsField'
import { isLikelyEmail, normalizeRecipients } from '../Notifications/recipients'
import { useMemo, useRef, useState } from 'react'
import { XIcon } from 'lucide-react'
import {
  useListExternalMessagingChannelsQuery,
  useGetNotificationRuleTemplateVariablesQuery,
  useRenderNotificationRulePreviewMutation,
  type Condition,
  type CreateNotificationRulePayload,
  type NotificationRule,
  type NotificationRuleMessagePreview,
  type NotificationTrigger,
  type TriggerEvent
} from '@axilon-apps/domains-ontology-ui-state'

import { ConditionBuilder } from '../triggers/ConditionBuilder'
import { conditionHasTagId } from '../triggers/condition-tag-ids'
import { newRcaTrigger, normalizeTriggerFields } from '../triggers/condition-defaults'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { InlineAlert } from '../../pages/ConfigurePage/components/DetailStates'
import { MessageTemplateField } from './MessageTemplateField'

const NO_TEAMS_CHANNEL = '__none__'
type TemplateFieldName =
  | 'message_title'
  | 'message_body'
  | 'clear_message_title'
  | 'clear_message_body'

/** Loose but practical email check — mirrors the proxy's `net/mail.ParseAddress` gate closely enough to catch typos before submit. */
export interface NotificationRuleFormProps {
  initialValue?: NotificationRule
  onSubmit: (payload: CreateNotificationRulePayload) => Promise<void> | void
  submitLabel?: string
  onCancel?: () => void
  busy?: boolean
  error?: string | null
}

export interface NotificationRuleFormValues {
  display_name: string
  message_title: string
  message_body: string
  clear_message_title: string
  clear_message_body: string
  email_addresses: string[]
  recipients: NotificationRecipients
  emailDraft: string
  teams_channel_id: string
  cooldown_seconds: number
  trigger_events?: TriggerEvent[] | null
  triggers: NotificationTrigger[]
}

function initialState(rule?: NotificationRule): NotificationRuleFormValues {
  return {
    display_name: rule?.display_name ?? '',
    message_title: rule?.message_title ?? '',
    message_body: rule?.message_body ?? '',
    clear_message_title: rule?.clear_message_title ?? '',
    clear_message_body: rule?.clear_message_body ?? '',
    email_addresses: rule?.email_addresses ?? [],
    recipients: normalizeRecipients(rule?.recipients),
    emailDraft: '',
    teams_channel_id: rule?.teams_channel_id ?? '',
    cooldown_seconds: rule?.cooldown_seconds ?? 300,
    trigger_events: rule ? (rule.trigger_events ?? null) : undefined,
    // Proxy requires a stable client-assigned trigger id (same gate as RCA/PM).
    triggers: rule?.triggers.length
      ? rule.triggers.map((t) => normalizeTriggerFields(t))
      : [newRcaTrigger({ name: '', purpose: '' })]
  }
}

export function buildNotificationRulePayload(
  form: NotificationRuleFormValues,
  mode: 'create' | 'update' = 'create'
): CreateNotificationRulePayload {
  const optionalText = (value: string) =>
    mode === 'update' ? value.trim() : value.trim() || undefined
  return {
    display_name: form.display_name.trim(),
    message_title: optionalText(form.message_title),
    message_body: optionalText(form.message_body),
    clear_message_title: optionalText(form.clear_message_title),
    clear_message_body: optionalText(form.clear_message_body),
    email_addresses: form.email_addresses,
    recipients: normalizeRecipients(form.recipients),
    teams_channel_id: optionalText(form.teams_channel_id),
    cooldown_seconds: form.cooldown_seconds,
    trigger_events: form.trigger_events,
    triggers: form.triggers.map((trigger) => normalizeTriggerFields(trigger))
  }
}

export function readNotificationTemplateErrors(error: unknown): {
  fields: Partial<Record<TemplateFieldName, string>>
  message: string
} {
  if (!error || typeof error !== 'object')
    return { fields: {}, message: 'Could not render preview.' }
  const data = 'data' in error && error.data && typeof error.data === 'object' ? error.data : null
  const message =
    data && 'message' in data && typeof data.message === 'string'
      ? data.message
      : 'Could not render preview.'
  const details =
    data && 'details' in data && data.details && typeof data.details === 'object'
      ? (data.details as Record<string, unknown>)
      : null
  const fields: Partial<Record<TemplateFieldName, string>> = {}
  for (const field of [
    'message_title',
    'message_body',
    'clear_message_title',
    'clear_message_body'
  ] as const) {
    const value = details && field in details ? details[field] : undefined
    if (typeof value === 'string') fields[field] = value
  }
  return { fields, message }
}

export function NotificationRuleForm({
  initialValue,
  onSubmit,
  submitLabel = 'Save',
  onCancel,
  busy,
  error
}: NotificationRuleFormProps) {
  const [form, setForm] = useState<NotificationRuleFormValues>(() => initialState(initialValue))
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [previewEvent, setPreviewEvent] = useState<TriggerEvent>('activated')
  const [preview, setPreview] = useState<NotificationRuleMessagePreview | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const previewRequestVersionRef = useRef(0)
  const [templateErrors, setTemplateErrors] = useState<Partial<Record<TemplateFieldName, string>>>(
    {}
  )

  const { data: channels = [], isLoading: channelsLoading } = useListExternalMessagingChannelsQuery(
    {
      platform: 'teams',
      page_size: 500
    }
  )
  const { data: templateVariableCatalog } = useGetNotificationRuleTemplateVariablesQuery()
  const [renderPreview, { isLoading: previewLoading }] = useRenderNotificationRulePreviewMutation()
  const templateVariables = templateVariableCatalog?.variables ?? []

  const allTriggersHaveTag =
    form.triggers.length > 0 && form.triggers.every((t) => conditionHasTagId(t.condition))
  const emailsValid = form.email_addresses.every(isLikelyEmail)

  // In-app delivery always works (recipients, or operators by default), so
  // email and Teams are optional.
  const canSubmit =
    form.display_name.trim().length > 0 && allTriggersHaveTag && emailsValid && !busy

  const showErr = submitAttempted

  const addEmailFromDraft = () => {
    const value = form.emailDraft.trim().replace(/,$/, '')
    if (!value) return
    setForm((f) => ({
      ...f,
      email_addresses: f.email_addresses.includes(value)
        ? f.email_addresses
        : [...f.email_addresses, value],
      emailDraft: ''
    }))
  }

  const removeEmail = (email: string) => {
    setForm((f) => ({
      ...f,
      email_addresses: f.email_addresses.filter((e) => e !== email)
    }))
  }

  const handleSubmit = async () => {
    setSubmitAttempted(true)
    if (!canSubmit) return
    try {
      await onSubmit(buildNotificationRulePayload(form, initialValue ? 'update' : 'create'))
    } catch (caught) {
      const parsed = readNotificationTemplateErrors(caught)
      if (Object.keys(parsed.fields).length > 0) {
        setTemplateErrors(parsed.fields)
        setPreviewError(parsed.message)
      }
    }
  }

  const setTemplateValue = (field: TemplateFieldName, value: string) => {
    previewRequestVersionRef.current += 1
    setForm((current) => ({ ...current, [field]: value }))
    setTemplateErrors((current) => {
      if (!current[field]) return current
      const next = { ...current }
      delete next[field]
      return next
    })
    setPreview(null)
    setPreviewError(null)
  }

  const handlePreview = async () => {
    const requestVersion = ++previewRequestVersionRef.current
    const requestedEvent = previewEvent
    setPreviewError(null)
    setTemplateErrors({})
    try {
      const result = await renderPreview({
        rule_id: initialValue?.id,
        display_name: form.display_name,
        message_title: form.message_title,
        message_body: form.message_body,
        clear_message_title: form.clear_message_title,
        clear_message_body: form.clear_message_body,
        event: requestedEvent,
        triggers: form.triggers.map((trigger) => normalizeTriggerFields(trigger))
      }).unwrap()
      if (requestVersion !== previewRequestVersionRef.current) return
      setPreview(result)
    } catch (caught) {
      if (requestVersion !== previewRequestVersionRef.current) return
      const parsed = readNotificationTemplateErrors(caught)
      setTemplateErrors(parsed.fields)
      setPreviewError(parsed.message)
      setPreview(null)
    }
  }

  const channelOptions = useMemo(
    () =>
      channels.map((c) => ({
        id: c.id,
        label: c.channel_name
          ? `${c.channel_name}${c.team_name ? ` (${c.team_name})` : ''}`
          : c.channel_id
      })),
    [channels]
  )

  return (
    <div className="flex flex-col gap-6">
      <Field
        label="Name"
        htmlFor="notif-rule-name"
        required
        invalid={showErr && form.display_name.trim().length === 0}
      >
        <Input
          id="notif-rule-name"
          value={form.display_name}
          onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
          placeholder="Pump 12 temperature alert"
          aria-invalid={showErr && form.display_name.trim().length === 0 ? true : undefined}
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-3">
        <MessageTemplateField
          id="notif-rule-title"
          label="Message title"
          value={form.message_title}
          onChange={(value) => setTemplateValue('message_title', value)}
          variables={templateVariables}
          hint="Shown as the email/Teams subject."
          placeholder="High temperature on {{trigger.name}}"
          error={templateErrors.message_title}
        />
        <Field
          label="Re-arm guard (seconds)"
          htmlFor="notif-rule-cooldown"
          hint="Minimum time between repeated deliveries after an edge. Defaults to 300s."
        >
          <Input
            id="notif-rule-cooldown"
            type="number"
            min={0}
            value={form.cooldown_seconds}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                cooldown_seconds: Number(e.target.value) || 0
              }))
            }
          />
        </Field>
        <Field
          label="Notify on"
          htmlFor="notif-rule-trigger-events"
          hint="Select which persisted trigger transitions this rule delivers. Legacy preserves level dispatch for older rules."
        >
          <Select
            value={
              form.trigger_events == null
                ? 'legacy'
                : form.trigger_events.length === 2
                  ? 'both'
                  : (form.trigger_events[0] ?? 'none')
            }
            onValueChange={(value) =>
              setForm((current) => ({
                ...current,
                trigger_events:
                  value === 'legacy'
                    ? null
                    : value === 'both'
                      ? ['activated', 'cleared']
                      : value === 'activated' || value === 'cleared'
                        ? [value]
                        : []
              }))
            }
          >
            <SelectTrigger id="notif-rule-trigger-events" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="legacy">Legacy level dispatch</SelectItem>
              <SelectItem value="activated">Activation</SelectItem>
              <SelectItem value="cleared">Return to normal</SelectItem>
              <SelectItem value="both">Activation and return to normal</SelectItem>
              <SelectItem value="none">No edge notifications</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>

      <MessageTemplateField
        id="notif-rule-body"
        label="Message body"
        value={form.message_body}
        onChange={(value) => setTemplateValue('message_body', value)}
        variables={templateVariables}
        hint="Optional plain text. Variables are resolved once for every delivery channel."
        placeholder="{{trigger.summary}} at {{event.occurred_at}}."
        multiline
        error={templateErrors.message_body}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <MessageTemplateField
          id="notif-rule-clear-title"
          label="Return-to-normal title"
          value={form.clear_message_title}
          onChange={(value) => setTemplateValue('clear_message_title', value)}
          variables={templateVariables}
          hint="Optional title used for cleared events."
          placeholder="{{rule.name}} returned to normal"
          error={templateErrors.clear_message_title}
        />
        <MessageTemplateField
          id="notif-rule-clear-body"
          label="Return-to-normal body"
          value={form.clear_message_body}
          onChange={(value) => setTemplateValue('clear_message_body', value)}
          variables={templateVariables}
          hint="Optional plain-text body used for cleared events."
          placeholder="{{trigger.summary}} cleared at {{event.occurred_at}}."
          multiline
          error={templateErrors.clear_message_body}
        />
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-line bg-bg-elev/40 p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <Field
            label="Message preview"
            htmlFor="notif-rule-preview-event"
            hint="Uses a synthetic event at the current server time."
          >
            <Select
              value={previewEvent}
              onValueChange={(value) => {
                if (value === 'activated' || value === 'cleared') {
                  previewRequestVersionRef.current += 1
                  setPreviewEvent(value)
                  setPreview(null)
                  setPreviewError(null)
                }
              }}
            >
              <SelectTrigger id="notif-rule-preview-event" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="activated">Activation</SelectItem>
                <SelectItem value="cleared">Return to normal</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Button
            type="button"
            variant="outline"
            onClick={() => void handlePreview()}
            disabled={previewLoading}
          >
            {previewLoading ? 'Rendering…' : 'Preview message'}
          </Button>
        </div>
        {preview && (
          <div
            className="flex flex-col gap-2 rounded border border-line bg-bg px-3 py-2"
            aria-live="polite"
          >
            <p className="m-0 text-xs font-medium uppercase tracking-wide text-ink-4">
              {preview.event} · {preview.occurred_at}
            </p>
            <p className="m-0 text-sm font-semibold text-ink">{preview.title || '(No title)'}</p>
            <p className="m-0 whitespace-pre-wrap text-sm text-ink-2">
              {preview.body || '(No body)'}
            </p>
          </div>
        )}
        {previewError && <InlineAlert kind="down">{previewError}</InlineAlert>}
      </div>

      <div className="flex flex-col gap-2">
        <div>
          <h2 className="m-0 text-sm font-semibold text-ink">Who gets this</h2>
          <p className="mt-0.5 m-0 text-xs text-ink-4">
            Each fire lands in their bell, and in their email if they keep email on.
          </p>
        </div>

        <Field label="People and roles">
          <RecipientsField
            value={form.recipients}
            onChange={(recipients) => setForm((f) => ({ ...f, recipients }))}
            disabled={busy}
            defaultLabel="operators (and everyone above them)"
          />
        </Field>

        <Field
          label="Other email addresses"
          htmlFor="notif-rule-email"
          hint="Outside addresses that get every fire, regardless of anyone's settings."
        >
          {form.email_addresses.length > 0 && (
            <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
              {form.email_addresses.map((email) => (
                <li
                  key={email}
                  className="inline-flex max-w-full items-center gap-1 rounded border border-line bg-bg-elev/60 px-2 py-1 text-xs"
                >
                  <span className="truncate">{email}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${email}`}
                    className="shrink-0 text-ink-4 hover:text-ink"
                    onClick={() => removeEmail(email)}
                  >
                    <XIcon className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Input
            id="notif-rule-email"
            value={form.emailDraft}
            onChange={(e) => setForm((f) => ({ ...f, emailDraft: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
                e.preventDefault()
                addEmailFromDraft()
              }
            }}
            onBlur={addEmailFromDraft}
            placeholder="operator@example.com — press Enter to add"
          />
          {showErr && !emailsValid && (
            <p className="m-0 text-xs text-st-down">One or more email addresses look invalid.</p>
          )}
        </Field>

        <Field label="Teams channel" htmlFor="notif-rule-teams">
          <Select
            value={form.teams_channel_id || NO_TEAMS_CHANNEL}
            onValueChange={(val) =>
              setForm((f) => ({
                ...f,
                teams_channel_id: val === NO_TEAMS_CHANNEL ? '' : val
              }))
            }
          >
            <SelectTrigger id="notif-rule-teams" className="w-full">
              <SelectValue
                placeholder={channelsLoading ? 'Loading channels…' : 'No Teams channel'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_TEAMS_CHANNEL}>No Teams channel</SelectItem>
              {channelOptions.map((opt) => (
                <SelectItem key={opt.id} value={opt.id}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {channelOptions.length === 0 && !channelsLoading && (
            <p className="m-0 text-xs text-ink-4">
              No connected Teams channels. Connect one under Settings → Integrations.
            </p>
          )}
        </Field>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 className="m-0 text-sm font-semibold text-ink">Triggers</h2>
            <p className="mt-0.5 m-0 text-xs text-ink-4">
              Every trigger is tracked independently; all active triggers count.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setForm((f) => ({
                ...f,
                triggers: [...f.triggers, newRcaTrigger({ name: '', purpose: '' })]
              }))
            }
          >
            + Add trigger
          </Button>
        </div>
        <div className="flex flex-col gap-3">
          {form.triggers.map((trigger, i) => (
            <div
              key={i}
              className="flex flex-col gap-3 rounded-lg border border-line bg-bg-elev/40 p-4"
            >
              <div className="flex flex-row items-end gap-2">
                <Field label="Purpose" htmlFor={`notif-trigger-${i}-purpose`} className="flex-1">
                  <Input
                    id={`notif-trigger-${i}-purpose`}
                    value={trigger.purpose ?? ''}
                    onChange={(e) =>
                      setForm((f) => {
                        const triggers = f.triggers.slice()
                        triggers[i] = {
                          ...triggers[i],
                          purpose: e.target.value
                        }
                        return { ...f, triggers }
                      })
                    }
                    placeholder="Temperature spike during steady state"
                  />
                </Field>
                {form.triggers.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        triggers: f.triggers.filter((_, j) => j !== i)
                      }))
                    }
                  >
                    Remove
                  </Button>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label="Activation delay (seconds)"
                  htmlFor={`notif-trigger-${i}-on-delay`}
                  hint="Condition must remain true for this long."
                >
                  <Input
                    id={`notif-trigger-${i}-on-delay`}
                    type="number"
                    min={0}
                    value={trigger.on_delay_seconds ?? ''}
                    onChange={(e) =>
                      setForm((f) => {
                        const triggers = f.triggers.slice()
                        triggers[i] = {
                          ...triggers[i],
                          on_delay_seconds:
                            e.target.value === '' ? undefined : Math.max(0, Number(e.target.value))
                        }
                        return { ...f, triggers }
                      })
                    }
                    placeholder="0"
                  />
                </Field>
                <Field
                  label="Clear delay (seconds)"
                  htmlFor={`notif-trigger-${i}-off-delay`}
                  hint="Condition must remain false for this long."
                >
                  <Input
                    id={`notif-trigger-${i}-off-delay`}
                    type="number"
                    min={0}
                    value={trigger.off_delay_seconds ?? ''}
                    onChange={(e) =>
                      setForm((f) => {
                        const triggers = f.triggers.slice()
                        triggers[i] = {
                          ...triggers[i],
                          off_delay_seconds:
                            e.target.value === '' ? undefined : Math.max(0, Number(e.target.value))
                        }
                        return { ...f, triggers }
                      })
                    }
                    placeholder="0"
                  />
                </Field>
              </div>
              <ConditionBuilder
                condition={trigger.condition}
                onChange={(next: Condition) =>
                  setForm((f) => {
                    const triggers = f.triggers.slice()
                    triggers[i] = { ...triggers[i], condition: next }
                    return { ...f, triggers }
                  })
                }
              />
              {showErr && !conditionHasTagId(trigger.condition) && (
                <p className="m-0 text-xs text-st-down">
                  Pick a tag for at least one condition in this trigger.
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {error && <InlineAlert kind="down">{error}</InlineAlert>}

      <div className="flex flex-row gap-2">
        <Button type="button" onClick={() => void handleSubmit()} disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  )
}

function Field({
  label,
  htmlFor,
  required,
  hint,
  invalid,
  className,
  children
}: {
  label: string
  htmlFor?: string
  required?: boolean
  hint?: string
  invalid?: boolean
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={`flex flex-col gap-1.5 ${className ?? ''}`}>
      <Label htmlFor={htmlFor}>
        {label}
        {required && <span className="text-st-down">*</span>}
      </Label>
      {children}
      {hint && <p className="m-0 text-xs text-ink-3">{hint}</p>}
      {invalid && <p className="m-0 text-xs text-st-down">This field is required.</p>}
    </div>
  )
}
